import type { ResolvedBrand } from "../form/colors";

const STYLE_ID = "redacto-dpdpa-form-styles";

function buildCss(brand: ResolvedBrand): string {
  return `
    :host {
      display: block;
      width: 100%;
      container-type: inline-size;
      container-name: dpdpa-card;
    }

    :host, :host > *, :host *, :host *::before, :host *::after {
      box-sizing: border-box;
    }

    .dpdpa-card {
      box-sizing: border-box;
      width: 100%;
      min-height: 520px;
      display: flex;
      flex-direction: column;
      padding: 28px 24px;
      background: ${brand.background};
      color: ${brand.text};
      border: 1px solid ${brand.border};
      border-radius: ${brand.borderRadius};
      font-family: ${brand.fontFamily};
      font-size: 15px;
      line-height: 1.5;
      text-align: left;
    }

    @container dpdpa-card (max-width: 420px) {
      .dpdpa-card {
        padding: 20px 16px;
        min-height: 480px;
      }
      .dpdpa-heading {
        font-size: 18px;
      }
      .dpdpa-success-title,
      .dpdpa-error-title {
        font-size: 16px;
      }
      .dpdpa-header {
        margin-bottom: 16px;
      }
    }

    @supports not (container-type: inline-size) {
      @media (max-width: 480px) {
        .dpdpa-card {
          padding: 20px 16px;
        }
        .dpdpa-heading {
          font-size: 18px;
        }
      }
    }

    .dpdpa-header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 20px;
    }

    .dpdpa-logo {
      max-height: 28px;
      max-width: 140px;
      display: inline-block;
      object-fit: contain;
    }

    .dpdpa-wordmark {
      font-weight: 700;
      font-size: 17px;
      color: ${brand.text};
      letter-spacing: 0.3px;
    }

    .dpdpa-footer {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid ${brand.border};
      font-size: 12px;
      color: ${brand.text};
      opacity: 0.7;
      text-align: center;
    }

    .dpdpa-form {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 14px;
      margin: 0;
    }

    .dpdpa-heading {
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 4px;
      color: ${brand.text};
      line-height: 1.3;
    }

    .dpdpa-subheading {
      margin: 0 0 8px;
      font-size: 14px;
      color: ${brand.text};
      opacity: 0.75;
    }

    .dpdpa-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .dpdpa-label {
      font-size: 13px;
      font-weight: 600;
      color: ${brand.text};
    }

    .dpdpa-input {
      width: 100%;
      min-width: 0;
      padding: 11px 13px;
      font-family: inherit;
      font-size: 14px;
      color: ${brand.text};
      background: #ffffff;
      border: 1px solid ${brand.border};
      border-radius: ${brand.borderRadius};
      outline: none;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .dpdpa-input:focus {
      border-color: ${brand.primary};
      box-shadow: 0 0 0 3px ${brand.primaryFocus};
    }

    .dpdpa-input:disabled {
      background: ${brand.border};
      opacity: 0.7;
      cursor: not-allowed;
    }

    .dpdpa-field-error,
    .dpdpa-general-error {
      font-size: 12px;
      color: ${brand.errorColor};
      line-height: 1.4;
    }

    .dpdpa-general-error {
      padding: 10px 12px;
      background: ${brand.errorColor}14;
      border: 1px solid ${brand.errorColor}33;
      border-radius: ${brand.borderRadius};
    }

    .dpdpa-btn-primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 18px;
      font-family: inherit;
      font-size: 15px;
      font-weight: 600;
      color: ${brand.primaryText};
      background: ${brand.primary};
      border: none;
      border-radius: ${brand.borderRadius};
      cursor: pointer;
      text-decoration: none;
      transition: background-color 0.15s ease, transform 0.05s ease;
      margin-top: 6px;
    }

    .dpdpa-btn-primary:hover:not(:disabled) {
      background: ${brand.primaryHover};
    }

    .dpdpa-btn-primary:active:not(:disabled) {
      transform: translateY(1px);
    }

    .dpdpa-btn-primary:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .dpdpa-btn-link {
      text-decoration: none;
      color: ${brand.primaryText};
    }

    .dpdpa-spinner {
      display: inline-block;
      border-radius: 50%;
      border-style: solid;
      border-color: ${brand.primaryText}4D;
      animation: dpdpa-spin 0.7s linear infinite;
    }

    .dpdpa-spinner-inline {
      width: 14px;
      height: 14px;
      border-width: 2px;
      border-top-color: ${brand.primaryText};
    }

    .dpdpa-polling {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 4px 2px 8px;
      gap: 16px;
    }

    .dpdpa-polling-sub {
      margin: 0;
      font-size: 13px;
      color: ${brand.text};
      opacity: 0.6;
      text-align: center;
    }

    .dpdpa-steps {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .dpdpa-step {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 15px;
      animation: dpdpa-step-in 0.35s ease-out both;
    }

    .dpdpa-step-done .dpdpa-step-label {
      color: ${brand.text};
      opacity: 0.6;
    }

    .dpdpa-step-icon {
      flex: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .dpdpa-step-icon-active {
      position: relative;
      width: 10px;
      height: 10px;
      margin: 0 4px;
      box-shadow: 0 0 0 0 ${brand.primaryFocus};
      animation: dpdpa-pulse 1.4s ease-out infinite;
    }

    .dpdpa-step-label {
      color: ${brand.text};
      font-weight: 500;
      word-break: break-word;
    }

    .dpdpa-shimmer {
      background: linear-gradient(
        90deg,
        ${brand.text} 0%,
        ${brand.primary} 40%,
        ${brand.accent} 50%,
        ${brand.primary} 60%,
        ${brand.text} 100%
      );
      background-size: 200% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: dpdpa-shimmer 2.2s linear infinite;
    }

    .dpdpa-dots {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      margin-left: 2px;
    }

    .dpdpa-dots > span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: ${brand.primary};
      opacity: 0.4;
      animation: dpdpa-dot-bounce 1.2s ease-in-out infinite;
    }

    .dpdpa-dots > span:nth-child(2) { animation-delay: 0.15s; }
    .dpdpa-dots > span:nth-child(3) { animation-delay: 0.3s; }

    @keyframes dpdpa-shimmer {
      from { background-position: 200% 0; }
      to   { background-position: -200% 0; }
    }

    @keyframes dpdpa-dot-bounce {
      0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
      40%           { opacity: 1;   transform: translateY(-3px); }
    }

    @keyframes dpdpa-pulse {
      0%   { box-shadow: 0 0 0 0 ${brand.primaryFocus}; }
      70%  { box-shadow: 0 0 0 8px rgba(0, 0, 0, 0); }
      100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
    }

    @keyframes dpdpa-step-in {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .dpdpa-progress {
      width: 100%;
      height: 6px;
      background: ${brand.border};
      border-radius: 999px;
      overflow: hidden;
      margin-top: 4px;
    }

    .dpdpa-progress-fill {
      height: 100%;
      background: linear-gradient(to right, ${brand.primary}, ${brand.accent});
      transition: width 0.5s ease;
    }

    .dpdpa-success {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      gap: 12px;
      padding: 8px 4px;
    }

    .dpdpa-check-circle {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dpdpa-success-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: ${brand.text};
      line-height: 1.35;
    }

    .dpdpa-success-sub {
      margin: 0;
      font-size: 14px;
      color: ${brand.text};
      opacity: 0.75;
    }

    .dpdpa-reset-link {
      font-size: 13px;
      color: ${brand.primary};
      text-decoration: none;
      margin-top: 4px;
      cursor: pointer;
    }

    .dpdpa-reset-link:hover {
      text-decoration: underline;
    }

    .dpdpa-error-view {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 12px;
      text-align: center;
      padding: 8px 4px;
    }

    .dpdpa-error-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: ${brand.errorColor};
    }

    .dpdpa-error-message {
      margin: 0;
      font-size: 14px;
      color: ${brand.text};
    }

    @keyframes dpdpa-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
}

export function injectFormStyles(
  root: ShadowRoot,
  brand: ResolvedBrand
): void {
  if (typeof document === "undefined") return;
  if (root.querySelector(`#${STYLE_ID}`)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = buildCss(brand);
  root.appendChild(style);
}
